import React, { useEffect, useState } from "react";
import { api, fileDownloadUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, FileSignature, Download, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Contracts() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [f, setF] = useState({ title: "", body_text: "", assigned_to: "" });
  const [file, setFile] = useState(null);
  const [signOpen, setSignOpen] = useState(null); // contract obj
  const [signName, setSignName] = useState("");
  const [agree, setAgree] = useState(false);

  const load = () => api.get("/contracts").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    if (user.role === "super_admin") api.get("/users").then((r) => setUsers(r.data));
  }, [user.role]);

  const create = async () => {
    if (!f.title || !f.assigned_to) return toast.error("Title and assignee required");
    if (!f.body_text && !file) return toast.error("Provide contract text or a PDF file");
    const fd = new FormData();
    fd.append("title", f.title);
    if (f.body_text) fd.append("body_text", f.body_text);
    fd.append("assigned_to", f.assigned_to);
    if (file) fd.append("file", file);
    await api.post("/contracts", fd);
    toast.success("Contract created & sent");
    setCreateOpen(false);
    setF({ title: "", body_text: "", assigned_to: "" });
    setFile(null);
    load();
  };

  const downloadSigned = async (c) => {
    try {
      const res = await api.get(`/contracts/${c.id}/signed-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Signed_Contract_${c.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download signed PDF"); }
  };

  const sign = async () => {
    if (!agree || !signName.trim()) return toast.error("Type your name and accept the terms");
    await api.post(`/contracts/${signOpen.id}/sign`, { signed_name: signName });
    toast.success("Contract signed");
    setSignOpen(null);
    setSignName("");
    setAgree(false);
    load();
  };

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-bold tracking-tight">Contracts</h1><p className="mt-1 text-muted-foreground">{isAdmin ? "Create and track contract signing." : "Review and sign your contracts."}</p></div>
        {isAdmin && <Button onClick={() => setCreateOpen(true)} data-testid="new-contract-btn"><Plus className="mr-2 h-4 w-4" />New Contract</Button>}
      </div>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead>{isAdmin && <TableHead>Assigned To</TableHead>}<TableHead>Document</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No contracts.</TableCell></TableRow>}
            {rows.map((c) => (
              <TableRow key={c.id} data-testid={`contract-row-${c.id}`}>
                <TableCell className="font-medium">{c.title}</TableCell>
                {isAdmin && <TableCell>{c.assigned_to_name}</TableCell>}
                <TableCell>
                  {c.file ? <a href={fileDownloadUrl(c.file.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary" data-testid={`contract-file-${c.id}`}><Download className="h-4 w-4" />PDF</a> : <span className="inline-flex items-center gap-1 text-muted-foreground"><FileText className="h-4 w-4" />Text</span>}
                </TableCell>
                <TableCell>
                  {c.status === "SIGNED"
                    ? <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">Signed</Badge>
                    : <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-400">Pending</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {!isAdmin && c.status !== "SIGNED" && <Button size="sm" onClick={() => setSignOpen(c)} data-testid={`sign-btn-${c.id}`}><FileSignature className="mr-2 h-4 w-4" />Review & Sign</Button>}
                  {c.status === "SIGNED" && !isAdmin && <span className="text-xs text-muted-foreground">by {c.signed_name}</span>}
                  {isAdmin && c.status === "SIGNED" && (
                    <div className="flex flex-col items-end gap-1">
                      <Button size="sm" variant="secondary" onClick={() => downloadSigned(c)} data-testid={`download-signed-pdf-${c.id}`}><Download className="mr-2 h-4 w-4" />Signed PDF</Button>
                      <span className="text-[11px] text-muted-foreground">by {c.signed_name} · {c.signed_at ? new Date(c.signed_at).toLocaleString() : ""}</span>
                    </div>
                  )}
                  {isAdmin && c.status !== "SIGNED" && <span className="text-xs text-muted-foreground">Awaiting signature</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Contract</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="contract-title" className="mt-1" /></div>
            <div>
              <Label>Assign To</Label>
              <Select value={f.assigned_to} onValueChange={(v) => setF({ ...f, assigned_to: v })}>
                <SelectTrigger className="mt-1" data-testid="contract-assignee"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>{users.filter((u) => u.role !== "super_admin").map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Contract Text (optional)</Label><Textarea rows={5} value={f.body_text} onChange={(e) => setF({ ...f, body_text: e.target.value })} data-testid="contract-text" className="mt-1" placeholder="Type the agreement terms here…" /></div>
            <div><Label>Or upload PDF (optional)</Label><Input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files[0])} data-testid="contract-pdf" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-contract-btn">Create & Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!signOpen} onOpenChange={(o) => !o && setSignOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{signOpen?.title}</DialogTitle></DialogHeader>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-border bg-accent p-4 text-sm">
            {signOpen?.body_text || "Please review the attached PDF document before signing."}
            {signOpen?.file && <a href={fileDownloadUrl(signOpen.file.file_id)} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-1 text-primary"><Download className="h-4 w-4" />Open PDF</a>}
          </div>
          <div><Label>Type your full legal name to sign</Label><Input value={signName} onChange={(e) => setSignName(e.target.value)} data-testid="sign-name" className="mt-1" /></div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="sign-agree" />I have read and agree to the terms of this contract.
          </label>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setSignOpen(null)}>Cancel</Button>
            <Button onClick={sign} data-testid="confirm-sign-btn">Sign Contract</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
